import React from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShiftTable from './ShiftTable';

const labels = {
  'missing-from-source': 'No longer present in source schedule; review required',
  'invalid-schedule': 'Missing or invalid scheduled times',
  'invalid-approved-times': 'Missing or invalid approved times',
  'approved-over-schedule': 'Approved hours more than 10% over scheduled hours',
  'clocked-over-schedule': 'Clocked hours more than 10% over scheduled hours',
  'incomplete-or-invalid-punches': 'Incomplete or invalid punch times',
  'missing-recorded-times': 'No complete recorded times',
  'missing-cost': 'Missing cost estimate',
  'unknown-pay-type': 'Staff pay type unknown'
};
const summaries = {
  'missing-recorded-times': 'missing recorded times',
  'incomplete-or-invalid-punches': 'incomplete or invalid punch times',
  'invalid-approved-times': 'invalid approved times',
  'approved-over-schedule': 'shifts with approved hours more than 10% over schedule',
  'clocked-over-schedule': 'shifts with clocked hours more than 10% over schedule',
  'invalid-schedule': 'invalid scheduled times',
  'unusable-times': 'shifts without usable times',
  'missing-from-source': 'missing from source schedule',
  'missing-cost': 'missing cost estimates',
  'unknown-pay-type': 'shifts with unknown pay type'
};
export default function ProblemShifts({site, canSeeCosts}) {
  const shifts = (site.problemShifts || []).map(shift => ({...shift,
    problems: shift.problems.filter(problem => canSeeCosts || problem !== 'missing-cost')
  })).filter(shift => shift.problems.length);
  const counts = {};
  for (const shift of shifts) {
    for (const problem of new Set(shift.problems)) counts[problem] = (counts[problem] || 0) + 1;
  }
  // Aggregates can also describe issues without individual details in older responses.
  const includeCount = (problem, count) => {
    if (count > 0) counts[problem] = Math.max(counts[problem] || 0, count);
  };
  includeCount('missing-recorded-times', site.missingRecordedIssueShifts ?? site.hoursProgress?.missingCompletedShifts ?? site.missingActualShifts);
  includeCount('invalid-schedule', site.invalidScheduleShifts);
  includeCount('unusable-times', site.unusableTimeIssueShifts ?? site.hoursProgress?.missingProjectionShifts ?? site.missingActualShifts);
  if (canSeeCosts) includeCount('missing-cost', site.missingCostShifts);
  const issues = Object.keys(summaries).filter(problem => counts[problem])
    .map(problem => {
      const count = counts[problem];
      const description = problem === 'missing-cost' ? `missing cost estimate${count === 1 ? '' : 's'}`
        : problem === 'approved-over-schedule' ? `shift${count === 1 ? '' : 's'} with approved hours more than 10% over schedule`
        : problem === 'clocked-over-schedule' ? `shift${count === 1 ? '' : 's'} with clocked hours more than 10% over schedule`
        : problem === 'unknown-pay-type' ? `shift${count === 1 ? '' : 's'} with unknown pay type`
        : problem === 'unusable-times' ? `shift${count === 1 ? '' : 's'} without usable times` : summaries[problem];
      return `${count} ${description}`;
    });
  const otherCount = Object.keys(counts).filter(problem => !summaries[problem]).reduce((sum, problem) => sum + counts[problem], 0);
  if (otherCount) issues.push(`${otherCount} other issues`);
  if (!issues.length) return null;
  const reportId = site.reportId || site.siteId;
  return <Accordion disableGutters elevation={0} slotProps={{transition: {unmountOnExit: true}}}
    sx={{bgcolor: '#b53d470d', border: '1px solid #b53d4733', borderRadius: '4px',
      '&:before': {display: 'none'}, '&.Mui-expanded': {margin: 0}}}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} id={`problems-${reportId}-heading`} aria-controls={`problems-${reportId}`}>
      <Typography variant="body2" sx={{color: '#b53d47', fontWeight: 600}}>ISSUES! - {issues.join(' · ')}</Typography>
    </AccordionSummary>
    <AccordionDetails id={`problems-${reportId}`}>
      <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
        Times are shown in Europe/London. Today's shifts, future shifts and ongoing shifts are not flagged just for missing punches.
      </Typography>
      {!shifts.length && <Typography variant="body2" color="text.secondary">Individual shift details are not available for these issues.</Typography>}
      {shifts.length > 0 && <ShiftTable shifts={shifts} name={`Problem shifts for ${site.name}`} issueLabels={labels} />}
    </AccordionDetails>
  </Accordion>;
}
